/* eslint-disable array-callback-return */
import { alpha, Box, List, ListItemButton, styled, Theme, useMediaQuery } from "@mui/material";
import Scrollbar from "components/ScrollBar";
import { Small } from "components/Typography";
import LayoutDrawer from "layouts/layout-parts/LayoutDrawer";
import SidebarProfile from "layouts/layout-parts/SidebarProfile";
import { FC, SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBox from "../../components/flexbox/FlexBox";
import sideMenuList from "../layout-parts/navList";
import AccordionMenu from "./AccordionMenu";
import useAuth from "../../hooks/useAuth";

type SideNavBarProps = {
  showSideBar: boolean;
  showMobileSideBar: boolean;
  closeMobileSideBar: () => void;
};

type activeMenu = { active: number };

const SIDEBAR_WIDTH = 264;

const SidebarWrapper = styled(Box)<{ show: number }>(({ theme, show }) => ({
  left: show ? 0 : -SIDEBAR_WIDTH,
  width: SIDEBAR_WIDTH,
  height: "100%",
  position: "fixed",
  borderRight: `1px solid ${theme.palette.divider}`,
  transition: "left 0.25s ease",
  zIndex: theme.zIndex.drawer,
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
}));

const StyledListItemButton = styled(ListItemButton)<activeMenu>(({ active, theme }) => {
  const { primary, text, action } = theme.palette;
  return {
    padding: "8px 12px",
    marginBottom: 2,
    borderRadius: 8,
    minHeight: 34,
    color: active ? primary.main : text.secondary,
    backgroundColor: active ? alpha(primary.main, 0.08) : "transparent",
    transition: "background-color 150ms ease, color 150ms ease",
    fontWeight: active ? 600 : 500,
    "&:hover": {
      backgroundColor: active ? alpha(primary.main, 0.1) : action.hover,
      color: active ? primary.main : text.primary,
    },
    "& .MuiSvgIcon-root": {
      color: active ? primary.main : text.secondary,
      fontSize: 17,
      transition: "color 150ms ease",
    },
    "&:hover .MuiSvgIcon-root": {
      color: active ? primary.main : text.primary,
    },
  };
});

const SubMenuItem = styled(FlexBox)<activeMenu>(({ theme, active }) => {
  const { primary, text, action } = theme.palette;
  return {
    cursor: "pointer",
    alignItems: "center",
    position: "relative",
    padding: "6px 10px 6px 30px",
    marginBottom: 2,
    borderRadius: 8,
    minHeight: 30,
    backgroundColor: active ? alpha(primary.main, 0.08) : "transparent",
    transition: "background-color 150ms ease",
    "& small": {
      color: active ? primary.main : text.secondary,
      fontWeight: active ? 600 : 500,
      fontSize: 13,
    },
    "&:hover": {
      backgroundColor: active ? alpha(primary.main, 0.1) : action.hover,
      "& small": { color: active ? primary.main : text.primary },
    },
  };
});

const SectionLabel = styled(Box)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
  padding: "16px 12px 8px",
}));

const DashboardSidebar: FC<SideNavBarProps> = (props) => {
  const { showSideBar, showMobileSideBar, closeMobileSideBar } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<string | boolean>("");
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down(1200));
  const { user } = useAuth();

  const activeAccordion = () => {
    sideMenuList.forEach((list) => {
      if (list.children && list.children.length > 0) {
        const findItem = list.children.find((item) => item.path === pathname);
        if (findItem) setExpanded(list.title);
      }
    });
  };

  useEffect(activeAccordion, [pathname]);

  const handleAccordionChange =
    (panel: string) => (_: SyntheticEvent, expand: boolean) => {
      setExpanded(expand ? panel : false);
    };

  const handleSubMenuItem = (path: string | undefined) => {
    if (path) navigate(path);
    if (downMd) closeMobileSideBar();
  };

  const activeRoute = (path: string) => (path === pathname ? 1 : 0);

  const sideBarContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Scrollbar autoHide clickOnTrack={false} style={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ px: 2, pt: 2.5, pb: 1.5 }}>
          <FlexBox alignItems="center" sx={{ pl: 1, mb: 2.5 }}>
            <Box
              component="img"
              src="/static/logo/listli_logo_full.png"
              alt="logo"
              sx={{
                height: 28,
                width: "auto",
                maxWidth: 160,
                filter: (t) =>
                  t.palette.mode === "dark" ? "brightness(0) invert(1)" : "none",
              }}
            />
          </FlexBox>

          <SectionLabel>{t("Меню")}</SectionLabel>

          <List sx={{ p: 0 }}>
            {sideMenuList.map((item, index) => {
              if (!user || !item.role.includes(user.role)) {
                return null;
              }

              if (item.children && item.children.length > 0) {
                return (
                  <AccordionMenu
                    key={index}
                    title={item.title}
                    expandedItem={expanded}
                    handleChange={handleAccordionChange}
                    accordionHeader={
                      <FlexBox alignItems="center" gap={1.25}>
                        <item.Icon sx={{ fontSize: 17 }} />
                        <Box
                          sx={{
                            fontSize: 13,
                            fontWeight: 550,
                            letterSpacing: "-0.005em",
                          }}
                        >
                          {t(item.title)}
                        </Box>
                      </FlexBox>
                    }
                  >
                    {item.children.map((menuItem: any, key) => {
                      if (!menuItem.subChildren && menuItem.role.includes(user.role)) {
                        const isActive = activeRoute(menuItem.path);
                        return (
                          <SubMenuItem
                            key={key}
                            active={isActive}
                            onClick={() => handleSubMenuItem(menuItem.path)}
                          >
                            <Small>{t(menuItem.name)}</Small>
                          </SubMenuItem>
                        );
                      }
                    })}
                  </AccordionMenu>
                );
              }

              return (
                <StyledListItemButton
                  key={index}
                  disableRipple
                  active={item.path === pathname ? 1 : 0}
                  onClick={() => {
                    setExpanded(false);
                    handleSubMenuItem(item.path);
                  }}
                >
                  <item.Icon sx={{ mr: 1.25 }} />
                  <Box
                    sx={{
                      fontSize: 13,
                      fontWeight: "inherit",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {t(item.title)}
                  </Box>
                </StyledListItemButton>
              );
            })}
          </List>
        </Box>
      </Scrollbar>

      <Box sx={{ p: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
        <SidebarProfile />
      </Box>
    </Box>
  );

  if (downMd) {
    return (
      <LayoutDrawer open={showMobileSideBar} onClose={closeMobileSideBar}>
        {sideBarContent}
      </LayoutDrawer>
    );
  }

  return <SidebarWrapper show={showSideBar ? 1 : 0}>{sideBarContent}</SidebarWrapper>;
};

export default DashboardSidebar;
